import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, TextField, Typography } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TurnstileWidget from '../components/TurnstileWidget';
import { getSignupInvitation, signUp } from '../utils/api';


const SignUp = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';
  const isPublicSignup = !inviteToken;
  const trialDays = isPublicSignup ? 14 : 90;
  const requestedNextPath = searchParams.get('next');
  const nextPath = requestedNextPath?.startsWith('/') && !requestedNextPath.startsWith('//')
    ? requestedNextPath
    : '/user-home';
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [signupStarted, setSignupStarted] = useState(isPublicSignup);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError('');
    if (!inviteToken) {
      setInvitation({ kind: 'public' });
      setLoading(false);
      return () => { alive = false; };
    }
    getSignupInvitation(inviteToken)
      .then((data) => {
        if (alive) setInvitation(data);
      })
      .catch((error) => {
        if (alive) setLoadError(error.data?.message || 'This invitation is no longer available.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [inviteToken]);

  const handleSignUp = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    if (!turnstileToken) {
      setErrorMessage('Please wait for the security check to finish.');
      return;
    }

    setIsSubmitting(true);
    try {
      await signUp({ inviteToken, email, username, password, turnstileToken });
      navigate(nextPath, { replace: true });
    } catch (error) {
      setErrorMessage(error.data?.message || 'Account creation could not be completed.');
      setTurnstileToken('');
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIsComplete = [
    email.trim() !== '' && email.includes('@'),
    username.trim() !== '',
    password.length >= 8 && confirmPassword.length >= 8 && password === confirmPassword,
  ];

  const handleNextStep = (event) => {
    event.preventDefault();
    setErrorMessage('');
    if (stepIsComplete[currentStep]) {
      setCurrentStep((step) => Math.min(step + 1, 2));
    }
  };

  const progressValue = [25, 50, 100][currentStep];

  const pinkPanelShadow = (theme, intensity = 0.6) => (
    `0 8px 15px ${alpha(theme.palette.secondary.main, intensity)}, ` +
    `0 20px 40px ${alpha(theme.palette.secondary.main, intensity / 2)}, ` +
    `0 35px 65px ${alpha(theme.palette.secondary.main, intensity / 6)}`
  );

  const cardSx = (theme) => ({
    width: '100%',
    maxWidth: 720,
    borderRadius: { xs: 4, sm: 6 },
    p: { xs: 2, sm: 3.5 },
    border: `4px solid ${theme.palette.green.main}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)}, ${theme.palette.background.default} 34%, ${alpha(theme.palette.secondary.main, 0.08)})`,
    boxShadow: pinkPanelShadow(theme),
  });

  const sectionSx = (theme,shadow=true) => ({
    border: `2px solid ${theme.palette.green.main}`,
    borderRadius: 3,
    backgroundColor: theme.palette.background.default,
    boxShadow: shadow ? pinkPanelShadow(theme) : 'none',
  });

  const compactTextSx = {
    fontSize: { xs: '0.55rem', sm: '0.65rem' },
    lineHeight: 1.7,
  };

  const actionButtonSx = (theme) => ({
    m: 0,
    py: 1.5,
    color: theme.palette.background.default,
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.green.main,
    boxShadow: `6px 6px 0 ${theme.palette.secondary.main}`,
    transition: 'transform 80ms ease, box-shadow 80ms ease, background-color 160ms ease',
    '&:hover': {
      color: theme.palette.background.default,
      backgroundColor: theme.palette.primary.main,
      transform: 'translate(-2px, -2px)',
      boxShadow: `8px 8px 0 ${theme.palette.secondary.main}, 0 0 18px ${alpha(theme.palette.primary.main, 0.4)}`,
    },
    '&:active': {
      transform: 'translate(2px, 2px)',
      boxShadow: `2px 2px 0 ${theme.palette.secondary.main}`,
    },
  });

  if (loading) {
    return <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        px: { xs: 1.5, sm: 2 },
        py: 4,
        '@keyframes signupPanelEnter': {
          from: { opacity: 0, transform: 'translateY(14px) scale(0.985)' },
          to: { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
      }}
    >
      <Box sx={cardSx}>
        {loadError ? (
          <>
            <Typography variant="h3" sx={{ color: 'secondary.main', mb: 2 }}>Invitation unavailable</Typography>
            <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
            <Button component={Link} to="/" variant="contained">Back to Dopatize</Button>
          </>
        ) : (
          <>


            <Box component="header" sx={{ display: 'grid', justifyItems: 'center', textAlign: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', justifyItems: 'center', textAlign: 'center',alignItems: 'center' }}>
              <Box
                component="img"
                src="/dope_logo_512.png"
                alt="Dopatize logo"
                sx={{
                  display: 'block',
                  width: { xs: '1.75rem', sm: '2rem' },
                  height: { xs: '1.75rem', sm: '2rem' },
                  objectFit: 'contain',
                  transform: 'rotate(90deg) scale(1.5)',
                  filter: 'drop-shadow(0 0 14px rgba(0, 229, 255, 0.35))',
                  mb: 1,
                  mr: 2,
                }}
              />
              <Typography variant="h2" sx={{ color: 'primary.main'}}>
                DOPATIZE
              </Typography>
              </Box>
              <Typography sx={{ ...compactTextSx, color: 'green.main', mt: 0.75 }}>
                COLLECT DOPAMINE, NOT GUILT
              </Typography>
              {invitation.kind === 'event' && (
                <Typography component="h1" sx={{ color: 'primary.main', fontSize: { xs: '0.85rem', sm: '1.05rem' }, lineHeight: 1.55, mt: 2 }}>
                  Sign up for Dopatize to add this event
                </Typography>
              )}
            </Box>

            {invitation.image && (
              <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                {invitation.kind === 'event' ? (
                  <Typography sx={{ color: 'secondary.main', fontSize: { xs: '0.65rem', sm: '0.8rem' }, lineHeight: 1.7, mb: 1.5 }}>
                    {invitation.event.title}
                  </Typography>
                ) : (
                  <Typography sx={{ color: 'secondary.main', fontSize: { xs: '0.65rem', sm: '0.8rem' }, lineHeight: 1.7, mb: 1.5 }}>
                    Your friend {invitation.inviter_username} wants you to join them in earning images by completing your todo list. This is one of their recent rewards!
                  </Typography>
                )}
                <Box
                  component="img"
                  src={invitation.image.image_url}
                  alt={invitation.image.image_class || 'Shared Dopatize reward'}
                  sx={(theme) => ({
                    display: 'block',
                    width: '100%',
                    maxWidth: 300,
                    aspectRatio: '1 / 1',
                    mx: 'auto',
                    objectFit: 'cover',
                    borderRadius: 3,
                    border: `4px solid ${theme.palette.green.main}`,
                    boxShadow: `6px 6px 0 ${darken(theme.palette.green.main, 0.55)}, 0 0 24px ${alpha(theme.palette.green.main, 0.22)}`,
                  })}
                />
              </Box>
            )}

            {!signupStarted && (
              <Box sx={{ animation: 'signupPanelEnter 280ms ease-out both', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
                <Box sx={(theme) => ({ ...sectionSx(theme), borderWidth: 4, p: { xs: 2, sm: 2.5 }, mb: 2.5, textAlign: 'center' })}>
                  <Typography sx={{ ...compactTextSx, color: 'green.main', letterSpacing: '0.08em', mb: 1 }}>
                    {invitation.kind === 'event'
                      ? 'CREATE YOUR ACCOUNT TO SAVE THIS EVENT'
                      : isPublicSignup
                        ? 'SMALL STEPS. SATISFYING PROGRESS.'
                        : `${invitation.inviter_username.toUpperCase()} UNLOCKED A FREE GIFT FOR YOU`}
                  </Typography>
                  <Typography component="h2" sx={{ color: 'primary.main', fontSize: { xs: '0.85rem', sm: '1.05rem' }, lineHeight: 1.55, mb: 1 }}>
                    SIGN UP NOW FOR <Box component="span" sx={{ color: 'secondary.main' }}>{trialDays} DAYS FREE</Box>
                  </Typography>
                  <Typography sx={{ ...compactTextSx, color: 'secondary.main' }}>
                    NO CREDIT CARD • FULL ACCESS • $0 TODAY
                  </Typography>
                  <Box sx={(theme) => ({ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: { xs: 0.75, sm: 3 }, mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.green.main, 0.4)}` })}>
                    {['Unlimited AI task generation', 'Unlimited reward images'].map((benefit) => (
                      <Box key={benefit} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                        <Check size={14} color="currentColor" aria-hidden="true" />
                        <Typography sx={{ ...compactTextSx, color: 'primary.main' }}>{benefit}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Button
                  id="start-sign-up"
                  type="button"
                  variant="contained"
                  onClick={() => setSignupStarted(true)}
                  endIcon={<ArrowRight size={18} />}
                  sx={(theme) => ({ ...actionButtonSx(theme), width: '100%', mb: 2.5 })}
                >
                  {isPublicSignup ? 'Start 14-Day Free Trial' : 'Claim 90 Days Free'}
                </Button>
              </Box>
            )}

            {isPublicSignup && signupStarted && (
              <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                <Typography component="h1" sx={{ color: 'primary.main', fontSize: { xs: '0.85rem', sm: '1.05rem' }, lineHeight: 1.7, mb: 1 }}>
                  Start your <Box component="span" sx={{ color: 'secondary.main' }}>14-day free trial</Box>
                </Typography>
                <Typography sx={{ ...compactTextSx, color: 'secondary.main' }}>
                  NO CREDIT CARD • FULL ACCESS • $0 TODAY
                </Typography>
              </Box>
            )}

            {signupStarted && (
              <Box
                key={currentStep}
                component="form"
                onSubmit={currentStep === 2 ? handleSignUp : handleNextStep}
                sx={(theme) => ({
                  ...sectionSx(theme),
                  p: { xs: 2, sm: 2.5 },
                  mb: 2.5,
                  display: 'grid',
                  gap: 2,
                  animation: 'signupPanelEnter 280ms ease-out both',
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                })}
              >
                {currentStep === 0 && (
                  <TextField
                    id="sign-up-email"
                    type="email"
                    label="Email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    autoFocus
                    inputProps={{ maxLength: 120 }}
                  />
                )}

                {currentStep === 1 && (
                  <TextField
                    id="sign-up-username"
                    label="Username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    required
                    autoFocus
                    inputProps={{ maxLength: 80 }}
                  />
                )}

                {currentStep === 2 && (
                  <>
                    <TextField
                      id="sign-up-password"
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      autoFocus
                      inputProps={{ minLength: 8 }}
                      helperText="Use at least 8 characters."
                    />
                    <TextField
                      id="sign-up-confirm-password"
                      type="password"
                      label="Confirm password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      required
                      autoFocus
                      error={confirmPassword !== '' && password !== confirmPassword}
                      helperText={confirmPassword !== '' && password !== confirmPassword ? 'Passwords do not match.' : 'Enter your password again.'}
                      inputProps={{ minLength: 8 }}
                    />
                    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                      <TurnstileWidget
                        action="invite_signup"
                        resetKey={turnstileResetKey}
                        onVerify={setTurnstileToken}
                        onExpire={() => setTurnstileToken('')}
                        onError={() => setTurnstileToken('')}
                      />
                    </Box>
                  </>
                )}

                {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      setErrorMessage('');
                      if (currentStep === 0) {
                        if (isPublicSignup) navigate('/');
                        else setSignupStarted(false);
                      } else {
                        setCurrentStep((step) => Math.max(step - 1, 0));
                      }
                    }}
                    startIcon={<ArrowLeft size={16} />}
                    sx={{ m: 0, flex: '0 0 auto' }}
                  >
                    Back
                  </Button>
                  <Button
                    id={currentStep === 2 ? 'sign-up-submit' : 'sign-up-next'}
                    type="submit"
                    variant="contained"
                    disabled={!stepIsComplete[currentStep] || (currentStep === 2 && (!turnstileToken || isSubmitting))}
                    endIcon={!isSubmitting ? <ArrowRight size={18} /> : null}
                    sx={(theme) => ({
                      ...actionButtonSx(theme),
                      ml: 'auto',
                      flex: currentStep === 2 ? 1 : '0 0 auto',
                      minWidth: currentStep === 2 ? 0 : 112,
                      px: currentStep === 2 ? 2 : 2.5,
                    })}
                  >
                    {currentStep === 2
                      ? (isSubmitting ? 'Launching…' : 'Launch Dopatize')
                      : 'Next'}
                  </Button>
                </Box>

                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    aria-label="Signup progress"
                    sx={(theme) => ({
                      height: 12,
                      borderRadius: 999,
                      border: `2px solid ${theme.palette.green.main}`,
                      backgroundColor: alpha(theme.palette.green.main, 0.12),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.green.main})`,
                      },
                    })}
                  />
                </Box>
              </Box>
            )}

            <Box sx={(theme) => ({ ...sectionSx(theme), p: 1.5, mb: 2.5, textAlign: 'center' })}>
              <Typography sx={{ ...compactTextSx, color: 'green.main', mb: 1.25, letterSpacing: '0.08em' }}>HOW DOPATIZE WORKS</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
                {['Create tasks', 'Complete tasks', 'Earn points', 'Get rewards'].map((step, index) => (
                  <Box key={step} sx={(theme) => ({ ...sectionSx(theme,false), p: 1.25, minHeight: 66, display: 'grid', placeItems: 'center', alignContent: 'center', gap: 0.75 })}>
                    <Typography sx={{ ...compactTextSx, color: index < 2 ? 'green.main' : 'secondary.main' }}>0{index + 1}</Typography>
                    <Typography sx={{ ...compactTextSx, color: 'primary.main' }}>{step}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Typography sx={{ ...compactTextSx, color: 'secondary.main', textAlign: 'center' }}>
              NO CREDIT CARD REQUIRED • INSTANT ACCESS
            </Typography>
            <Typography sx={{ ...compactTextSx, color: 'text.secondary', textAlign: 'center', mt: 1.5 }}>
              Already have an account? <Link to={`/sign-in?next=${encodeURIComponent(nextPath)}`}>Sign in</Link>
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SignUp;
